import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { PlusCircle, ArrowRight } from "lucide-react";

export default function PortalOverview() {
  const [wos, setWos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/work-orders"), api.get("/invoices")]).then(([wo, inv]) => {
      setWos(wo.data);
      setInvoices(inv.data);
      setLoading(false);
    });
  }, []);

  const stats = {
    active: wos.filter((w) => ["pending", "assigned", "in_progress"].includes(w.status)).length,
    completed: wos.filter((w) => w.status === "completed").length,
    total: wos.length,
    outstanding: invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + Number(i.amount), 0),
  };

  return (
    <div className="space-y-8" data-testid="portal-overview">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Portal overview</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-medium mt-2">
            Your operations, at a glance.
          </h1>
        </div>
        <Link
          to="/portal/work-orders/new"
          data-testid="overview-new-wo-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm"
        >
          <PlusCircle className="h-4 w-4" /> New work order
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <Stat label="Active work orders" value={stats.active} testId="stat-active" />
        <Stat label="Completed" value={stats.completed} testId="stat-completed" />
        <Stat label="Total submitted" value={stats.total} testId="stat-total" />
        <Stat label="Outstanding" value={`$${stats.outstanding.toLocaleString()}`} testId="stat-outstanding" />
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="font-display text-lg tracking-tight">Recent work orders</div>
          <Link to="/portal/work-orders" className="text-xs font-semibold text-[#1E40AF] inline-flex items-center gap-1">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">ID</th>
              <th className="text-left px-5 py-3 font-semibold">Property</th>
              <th className="text-left px-5 py-3 font-semibold">Service</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="5" className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && wos.length === 0 && (
              <tr>
                <td colSpan="5" className="px-5 py-10 text-center text-slate-400">
                  No work orders yet. <Link to="/portal/work-orders/new" className="text-[#1E40AF] font-semibold">Create your first one.</Link>
                </td>
              </tr>
            )}
            {wos.slice(0, 5).map((wo) => (
              <tr key={wo.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-alt text-xs text-slate-500">{wo.id.slice(-6).toUpperCase()}</td>
                <td className="px-5 py-3">
                  <Link
                    to={`/portal/work-orders/${wo.id}`}
                    data-testid={`wo-link-${wo.id}`}
                    className="text-slate-900 hover:text-[#1E40AF] font-medium"
                  >
                    {wo.property_address}
                  </Link>
                </td>
                <td className="px-5 py-3 text-slate-600">{wo.service_type}</td>
                <td className="px-5 py-3"><StatusBadge status={wo.status} /></td>
                <td className="px-5 py-3 text-right text-xs text-slate-500 tabular-nums">
                  {new Date(wo.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="font-display text-3xl tracking-tighter tabular-nums mt-2 text-[#0B192C]">{value}</div>
    </div>
  );
}
